import React, { useEffect, useRef, useState } from 'react';
import confetti from 'canvas-confetti';
import { WORD_BUILDER_ITEMS, WordItem } from '../../core/curriculum/curriculumData';
import { UPPERCASE_LETTER_STROKES } from '../../core/writing/letterStrokesData';
import { PixiLetterTracingCanvas } from '../writing/PixiLetterTracingCanvas';
import { speechProvider } from '../../core/speech/speechProvider';
import { appRepository } from '../../core/storage/repository';

interface WordWritingGameProps {
  onExit: () => void;
}

type LetterStatus = 'pending' | 'wrong' | 'correct';

const WORD_ROUNDS = WORD_BUILDER_ITEMS.filter(item =>
  item.word.split('').every(letter => Boolean(UPPERCASE_LETTER_STROKES[letter])),
);

export const WordWritingGame: React.FC<WordWritingGameProps> = ({ onExit }) => {
  const [round, setRound] = useState(0);
  const currentWord: WordItem = WORD_ROUNDS[round % WORD_ROUNDS.length];
  const letters = currentWord.word.split('');
  const [statuses, setStatuses] = useState<LetterStatus[]>(() => letters.map(() => 'pending'));
  const statusesRef = useRef<LetterStatus[]>(letters.map(() => 'pending'));

  useEffect(() => {
    const nextStatuses = letters.map(() => 'pending' as LetterStatus);
    statusesRef.current = nextStatuses;
    setStatuses(nextStatuses);
    speechProvider.speak(`Ayo tulis kata ${currentWord.word}!`, { rate: 0.85 });
  }, [round, currentWord.word]);

  const completedCount = statuses.filter(status => status === 'correct').length;
  const isWordComplete = completedCount === letters.length;

  const handleLetterCompleted = (index: number) => {
    if (statusesRef.current[index] === 'correct') return;

    const nextStatuses = [...statusesRef.current];
    nextStatuses[index] = 'correct';
    statusesRef.current = nextStatuses;
    setStatuses(nextStatuses);

    if (nextStatuses.every(status => status === 'correct')) {
      speechProvider.playChime('celebrate');
      speechProvider.speak(`Hebat! Kata ${currentWord.word} sudah selesai ditulis!`, { rate: 0.88 });
      appRepository.recordWordMastered(currentWord.word);
      confetti({ particleCount: 55, spread: 75, origin: { y: 0.55 } });
    } else {
      speechProvider.playChime('success');
      speechProvider.speak(`Bagus! Huruf ${letters[index]} sudah benar.`, { rate: 0.88 });
    }
  };

  const handleLetterIncomplete = (index: number) => {
    if (statusesRef.current[index] === 'correct') return;
    const nextStatuses = [...statusesRef.current];
    nextStatuses[index] = 'wrong';
    statusesRef.current = nextStatuses;
    setStatuses(nextStatuses);
    speechProvider.playChime('tap');
    speechProvider.speak(`Hampir! Coba tulis huruf ${letters[index]} lagi, yuk.`, { rate: 0.88 });
  };

  const handleNextWord = () => {
    speechProvider.playChime('tap');
    setRound(previous => previous + 1);
  };

  return (
    <div className="w-full max-w-5xl mx-auto">
      <div className="bg-white rounded-3xl p-4 md:p-6 shadow-xl border-4 border-amber-200">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <p className="text-xs font-black uppercase tracking-wide text-amber-600">Tulis Kata</p>
            <h2 className="text-2xl md:text-3xl font-display font-black text-[#243047]">
              Gambar setiap hurufnya
            </h2>
          </div>
          <div className="rounded-2xl bg-amber-50 border-2 border-amber-200 px-3 py-2 text-sm font-bold text-amber-900">
            {completedCount}/{letters.length} huruf benar
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 md:gap-4 rounded-2xl bg-amber-50 border-2 border-amber-100 p-3 mb-5">
          <span className="text-4xl md:text-5xl" aria-hidden="true">{currentWord.emoji}</span>
          <div>
            <div className="flex flex-wrap gap-1.5" aria-label={`Kata ${currentWord.word}`}>
              {letters.map((letter, index) => (
                <span
                  key={`${letter}-${index}`}
                  className={`min-w-10 h-12 px-2 rounded-xl border-2 flex items-center justify-center font-display font-black text-2xl ${
                    statuses[index] === 'correct'
                      ? 'bg-emerald-500 border-emerald-600 text-white'
                      : statuses[index] === 'wrong'
                        ? 'bg-rose-50 border-rose-300 text-rose-700'
                        : 'bg-white border-amber-300 text-amber-950'
                  }`}
                >
                  {letter}
                  {statuses[index] === 'correct' && <span className="text-base ml-1">✓</span>}
                </span>
              ))}
            </div>
            <p className="text-xs text-gray-600 mt-1">{currentWord.category} · {currentWord.sentenceExample}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {letters.map((letter, index) => {
            const status = statuses[index];
            return (
              <section
                key={`${currentWord.id}-${letter}-${index}`}
                className={`rounded-2xl border-2 p-2 transition-colors ${
                  status === 'correct'
                    ? 'border-emerald-300 bg-emerald-50'
                    : status === 'wrong'
                      ? 'border-rose-300 bg-rose-50'
                      : 'border-slate-200 bg-slate-50'
                }`}
              >
                <div className="flex items-center justify-between px-2 mb-1">
                  <span className="font-display font-black text-xl text-slate-800">{letter}</span>
                  <span className={`text-xs font-black ${status === 'correct' ? 'text-emerald-700' : status === 'wrong' ? 'text-rose-700' : 'text-slate-500'}`}>
                    {status === 'correct' ? 'Benar ✓' : status === 'wrong' ? 'Coba lagi' : 'Tulis di sini'}
                  </span>
                </div>
                <PixiLetterTracingCanvas
                  key={`${currentWord.id}-${letter}-${index}`}
                  letter={letter}
                  isUppercase
                  strokes={UPPERCASE_LETTER_STROKES[letter]}
                  onLetterCompleted={() => handleLetterCompleted(index)}
                  onStrokeIncomplete={() => handleLetterIncomplete(index)}
                />
              </section>
            );
          })}
        </div>

        {isWordComplete && (
          <div
            role="status"
            className="mt-5 rounded-3xl border-4 border-emerald-300 bg-emerald-50 px-4 py-5 text-center shadow-sm"
          >
            <div className="text-4xl mb-1" aria-hidden="true">🎉</div>
            <h3 className="text-2xl font-display font-black text-emerald-800">
              Hebat sekali!
            </h3>
            <p className="mt-1 text-emerald-900 font-bold">
              Kata {currentWord.word} sudah selesai kamu tulis dengan benar.
            </p>
          </div>
        )}

        <div className="flex flex-wrap justify-center gap-3 mt-5">
          <button
            onClick={onExit}
            className="px-5 py-3 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold transition"
          >
            Pilih Game Lain
          </button>
          {isWordComplete && (
            <button
              onClick={handleNextWord}
              className="px-6 py-3 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold shadow-md transition"
            >
              Kata Berikutnya ➜
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
